import { useEffect, useState } from "react";
import api from "../lib/apiClient";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { UserPlus, LogOut, Trash2, ArrowLeft, Loader2, Shield, KeyRound } from "lucide-react";
import { toast, Toaster } from "sonner";

export default function Users() {
  const nav = useNavigate();
  const [users, setUsers] = useState([]);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ email: "", password: "" });
  const [saving, setSaving] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [pwSaving, setPwSaving] = useState(false);
  const [openDelete, setOpenDelete] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: u }, { data: m }] = await Promise.all([
        api.get("/admin/users"),
        api.get("/auth/me"),
      ]);
      setUsers(u);
      setMe(m);
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem("ingelec_admin_token");
        nav("/admin/login");
      } else toast.error("Erreur de chargement");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const logout = () => {
    localStorage.removeItem("ingelec_admin_token");
    nav("/admin/login");
  };

  const create = async () => {
    if (!form.email || !form.password) { toast.error("Email et mot de passe requis"); return; }
    if (form.password.length < 8) { toast.error("Mot de passe : 8 caractères minimum"); return; }
    setSaving(true);
    try {
      await api.post("/admin/users", form);
      toast.success("Utilisateur créé");
      setCreating(false);
      setForm({ email: "", password: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de création");
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!openDelete) return;
    try {
      await api.delete(`/admin/users/${openDelete.id}`);
      toast.success("Utilisateur supprimé");
      setOpenDelete(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de suppression");
    }
  };

  const changePassword = async () => {
    if (pwForm.new_password.length < 8) { toast.error("Nouveau mot de passe : 8 caractères minimum"); return; }
    if (pwForm.new_password !== pwForm.confirm) { toast.error("Les mots de passe ne correspondent pas"); return; }
    setPwSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: pwForm.current_password, new_password: pwForm.new_password });
      toast.success("Mot de passe modifié");
      setPwOpen(false);
      setPwForm({ current_password: "", new_password: "", confirm: "" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setPwSaving(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="page-admin-users">
      <Toaster position="top-right" />
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/admin" className="text-slate-500 hover:text-[#1B3A6B] flex items-center gap-1.5 text-sm" data-testid="users-back-admin">
              <ArrowLeft className="w-4 h-4" /> Catalogue
            </Link>
            <div className="h-5 w-px bg-slate-200" />
            <span className="font-display font-bold text-[#1B3A6B]">Admin · Utilisateurs</span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setPwOpen(true)} data-testid="users-change-password">
              <KeyRound className="w-4 h-4 mr-1.5" /> Mon mot de passe
            </Button>
            <Button onClick={() => setCreating(true)} className="bg-[#FBBF24] hover:bg-[#F59E0B] text-[#0F172A] font-semibold rounded-full" data-testid="users-add-btn">
              <UserPlus className="w-4 h-4 mr-1.5" /> Inviter
            </Button>
            <Button variant="ghost" onClick={logout} data-testid="users-logout">
              <LogOut className="w-4 h-4 mr-1.5" /> Déconnexion
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          {loading ? (
            <div className="p-10 flex items-center justify-center text-slate-500"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Chargement…</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Rôle</TableHead>
                  <TableHead>Créé par</TableHead>
                  <TableHead>Créé le</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u) => {
                  const isMe = me && me.id === u.id;
                  return (
                    <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                      <TableCell className="font-medium text-slate-900">
                        {u.email} {isMe && <span className="ml-1 text-xs text-slate-400">(vous)</span>}
                      </TableCell>
                      <TableCell>
                        {u.is_super ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#1B3A6B]/10 text-[#1B3A6B] text-xs font-semibold">
                            <Shield className="w-3 h-3" /> Super-admin
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 text-xs">Admin</span>
                        )}
                      </TableCell>
                      <TableCell className="text-slate-500 text-sm">{u.created_by || "—"}</TableCell>
                      <TableCell className="text-slate-500 text-sm">{new Date(u.created_at).toLocaleDateString("fr-FR")}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setOpenDelete(u)}
                          disabled={u.is_super || isMe}
                          data-testid={`user-delete-${u.id}`}
                          title={u.is_super ? "Super-admin protégé" : isMe ? "Vous ne pouvez pas vous supprimer" : "Supprimer"}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </div>
        <p className="mt-4 text-xs text-slate-500">
          Le super-admin est créé automatiquement à partir des variables d'environnement et ne peut pas être supprimé.
          Tous les utilisateurs ont les mêmes droits (gestion du catalogue et lecture des demandes contact).
        </p>
      </div>

      {/* Create user dialog */}
      <Dialog open={creating} onOpenChange={(o) => !o && setCreating(false)}>
        <DialogContent data-testid="users-create-dialog">
          <DialogHeader><DialogTitle>Inviter un utilisateur</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label>Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" data-testid="users-create-email" />
            </div>
            <div>
              <Label>Mot de passe initial</Label>
              <Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1.5" placeholder="8 caractères minimum" data-testid="users-create-password" />
              <p className="mt-1.5 text-xs text-slate-500">Transmettez-le à la personne. Elle pourra le changer après connexion.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreating(false)} data-testid="users-create-cancel">Annuler</Button>
            <Button onClick={create} disabled={saving} className="bg-[#1B3A6B] hover:bg-[#142c54]" data-testid="users-create-submit">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Créer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete dialog */}
      <Dialog open={!!openDelete} onOpenChange={(o) => !o && setOpenDelete(null)}>
        <DialogContent data-testid="users-delete-dialog">
          <DialogHeader><DialogTitle>Supprimer cet utilisateur ?</DialogTitle></DialogHeader>
          <p className="text-slate-600">"{openDelete?.email}" perdra immédiatement l'accès à l'admin.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenDelete(null)} data-testid="users-delete-cancel">Annuler</Button>
            <Button onClick={remove} className="bg-red-600 hover:bg-red-700" data-testid="users-delete-confirm">Supprimer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change password dialog */}
      <Dialog open={pwOpen} onOpenChange={(o) => !o && setPwOpen(false)}>
        <DialogContent data-testid="users-pw-dialog">
          <DialogHeader><DialogTitle>Changer mon mot de passe</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label>Mot de passe actuel</Label>
              <Input type="password" value={pwForm.current_password} onChange={(e) => setPwForm({ ...pwForm, current_password: e.target.value })} className="mt-1.5" data-testid="users-pw-current" />
            </div>
            <div>
              <Label>Nouveau mot de passe</Label>
              <Input type="password" value={pwForm.new_password} onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })} className="mt-1.5" placeholder="8 caractères minimum" data-testid="users-pw-new" />
            </div>
            <div>
              <Label>Confirmer le nouveau mot de passe</Label>
              <Input type="password" value={pwForm.confirm} onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })} className="mt-1.5" data-testid="users-pw-confirm" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPwOpen(false)} data-testid="users-pw-cancel">Annuler</Button>
            <Button onClick={changePassword} disabled={pwSaving} className="bg-[#1B3A6B] hover:bg-[#142c54]" data-testid="users-pw-submit">
              {pwSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
